/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useMemo} from 'react'
import {Box, Container, Flex} from '@chakra-ui/react'
import {
    AuthHelpers,
    ShopperCustomersMutations,
    useAuthHelper,
    useCustomerType,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import useDoubleOptIn from '../../hooks/use-double-opt-in'
import {useOcapiSubscribeToNewsletterMutation} from '../../hooks/useOcapi'
import Seo from '../../components/seo'
import {useForm} from 'react-hook-form'
import RegisterForm from '../../components/register'
import {useIntl} from 'react-intl'
import useEinstein from '../../hooks/use-einstein'
import {useLocation} from 'react-router-dom'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import useInteractionStudioPixel from '../../tracking/interaction-studio'
import {ACCOUNT_HREF, HYBRID_ACCOUNT, HYBRID_AUTH, SIGN_IN_HREF} from '../../constants'
import useHybridURL from '../../hooks/use-hybrid-url'
import useEffectEvent from '../../hooks/use-effect-event'
import {trackEvent} from '../../tracking/analytics'
import HeaderContent from '../../components/header-content'
import CMSContent from '../../components/cms-content'
import useCmsContent from '../../components/cms-content/hooks/use-cms-content'
import useRegistrationErrorMessage from '../../hooks/use-error-message'
import {useToast} from '../../hooks/use-toast'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const Registration = () => {
    const hybridURL = useHybridURL()
    const {isRegistered} = useCustomerType()
    const form = useForm()
    const {formatMessage} = useIntl()
    const einstein = useEinstein()
    const {pathname} = useLocation()
    const {isDoubleOptInEnabled} = useDoubleOptIn()
    const errorMsg = useRegistrationErrorMessage()

    const registerWithLogin = useAuthHelper(AuthHelpers.Register)
    const registerWithoutLogin = useShopperCustomersMutation(
        ShopperCustomersMutations.RegisterCustomer
    )
    const {mutateAsync: subscribeToNewsletter} = useOcapiSubscribeToNewsletterMutation()

    useInitialDataLayer({pageType: 'Registration'})
    useInteractionStudioPixel()

    /**************** Page Cache ****************/
    const {res} = useServerContext()
    if (res) {
        const cacheConfig = getConfig().app.cache
        if (cacheConfig?.registration) {
            res.set('Cache-Control', cacheConfig.registration)
        }
    }

    const {data: preloadedContent} = useCmsContent('mystokke-registration')

    const processFormError = async (error) => {
        const message = await errorMsg(error)
        form.setError('global', {
            type: 'manual',
            message
        })
        window.scrollTo({top: 0, behavior: 'smooth'})
    }

    const submitForm = async (data) => {
        form.clearErrors('global')

        const body = {
            customer: {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                login: data.email
            },
            password: data.password
        }

        // intentionally not awaiting the promise here as we want to run in parallel with the newsletter subscription and tracking
        const registerPromise = !isDoubleOptInEnabled(data.email)
            ? registerWithLogin.mutateAsync(body, {
                  onError: processFormError
              })
            : registerWithoutLogin.mutateAsync(
                  {body},
                  {
                      onError: processFormError
                  }
              )

        // intentionally not awaiting the promise here as we want to run in parallel with the registration and tracking
        const newsletterPromise = data.newsletter
            ? subscribeToNewsletter({
                  parameters: {
                      email: data.email,
                      firstName: data.firstName,
                      lastName: data.lastName
                  }
              })
            : true
        // intentionally not awaiting the promise here as we want to run in parallel with the registration and newsletter subscription
        const trackEventPromise = trackEvent({
            event: 'account_create',
            email: data.email
        })
        // wait for tracking and registration to complete, as otherwise the user might be redirected before the tracking is sent
        await Promise.allSettled([registerPromise, trackEventPromise, newsletterPromise])
    }

    // If customer is registered push to account page
    const onCustomerChange = useEffectEvent(() => hybridURL(HYBRID_ACCOUNT, ACCOUNT_HREF))
    useEffect(() => {
        if (isRegistered) {
            onCustomerChange()
        }
    }, [isRegistered, onCustomerChange])

    /**************** Einstein ****************/
    const onMount = useEffectEvent(() => einstein.sendViewPage(pathname))
    useEffect(() => {
        onMount()
    }, [onMount])

    const toast = useToast()
    const email = form.getValues().email
    const doiEnabled = useMemo(() => isDoubleOptInEnabled(email), [isDoubleOptInEnabled, email])
    useEffect(() => {
        if (doiEnabled && form.formState.isSubmitted && form.formState.isSubmitSuccessful) {
            toast({
                title: `${formatMessage({
                    defaultMessage:
                        'In order to login, you have to activate your account from the link we sent you in an email.',
                    id: 'account-registration.msg.double_opt_in'
                })}`
            })
        }
    }, [
        doiEnabled,
        form.formState.isSubmitSuccessful,
        form.formState.isSubmitted,
        formatMessage,
        toast
    ])

    return (
        <>
            <HeaderContent />
            <Box data-testid="registration-page" bg="stokkeCore.white">
                <Seo
                    title={formatMessage({
                        id: 'account_registration.page.title',
                        defaultMessage: 'Create a Stokke® Account | Stokke® Online Shop'
                    })}
                    description="Customer sign up"
                />
                <Flex
                    maxWidth="96rem"
                    marginRight="auto"
                    marginLeft="auto"
                    flexDirection={{base: 'column', md: 'row'}}
                    gap={{base: 4, lg: 6, xl: 8}}
                    padding={{base: 4, lg: 6, xl: 8}}
                    justifyContent="center"
                >
                    <Box width="100%" minWidth={0} flexBasis={{md: '100%'}}>
                        {preloadedContent?.c_body !== '' && (
                            <Container
                                p="0"
                                backgroundColor="stokkeCore.white"
                                height="fit-content"
                                margin="0"
                                // could be removed width new cms module for login/registration
                                sx={{'section>div>div.sk-paragraph-wrapper': {maxWidth: '100%'}}}
                            >
                                <CMSContent
                                    id="mystokke-registration"
                                    content={preloadedContent}
                                    showError={false}
                                />
                            </Container>
                        )}
                    </Box>
                    <Container
                        width="100%"
                        flexBasis={{md: '100%', xl: '31.25rem', '2xl': '37.5rem'}}
                        minWidth={{base: '100%', md: 'auto', xl: '31.25rem', '2xl': '37.5rem'}}
                        maxWidth="37.5rem"
                        padding="0"
                        bg="white"
                        borderRadius="base"
                        backgroundColor="stokkePink.bright"
                    >
                        <RegisterForm
                            submitForm={submitForm}
                            form={form}
                            clickSignIn={() => {
                                hybridURL(HYBRID_AUTH, SIGN_IN_HREF)
                            }}
                            variant="page"
                        />
                    </Container>
                </Flex>
            </Box>
        </>
    )
}

Registration.getTemplateName = () => 'registration'

export default Registration
